import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { isAxiosError } from 'axios';
import { ExternalLink } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import type {
  ReservationHolder,
  ClosedStockTakeRefusal,
  StaleStockLine,
  StockTakeDetail,
  StockTakeOrigin,
  StockTakeRound,
} from '@/infrastructure/FulfillmentAdminClient';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import { StockClient } from '@/infrastructure/StockClient';
import { useAuth } from '@/providers/AuthProvider';
import { productAttributesClient, type BarcodeOwner } from '@/infrastructure/ProductAttributesClient';
import {
  MISSING,
  UNASSIGNED_LINE,
  adjustReasonFor,
  auditTally,
  COUNT_ERROR,
  countInputError,
  countedDiff,
  inLine,
  isValidCount,
  MAX_COUNT,
  inRoute,
  keepOrder,
  keyOf,
  locationLines,
  matchesQuery,
  mergeProductInfo,
  routeLabel,
  routesText,
  sortRows,
  sourcingRouteOptions,
  stockIntegritySummary,
  stockStatusCsv,
  LOCATION_CODE_ERROR,
  UNASSIGNED_SHELF,
  auditCsv,
  isUnassignedShelf,
  shelfInputValue,
  shelfLabel,
  isLocationCode,
  isOversold,
  normalizeLocationCode,
  searchedCode,
  oversoldSkus,
  withOnHand,
} from './inventoryView';
import type {
  AdjustReason,
  InventoryScreenRow,
  InventoryTab,
  SortMode,
} from '@/pages/fulfillment/inventory/inventoryView';
import { AbandonAuditModal } from './components/AbandonAuditModal';
import { ConfirmAuditModal } from './components/ConfirmAuditModal';
import type { AuditDifference, AuditShortfall } from './components/ConfirmAuditModal';
import { MovementFormPane } from './components/MovementFormPane';
import { LocationImportModal } from './components/LocationImportModal';
import { NeedsFixingFirst } from './components/NeedsFixingFirst';
import { PastAuditLogsModal } from './components/PastAuditLogsModal';
import { ReservedOrdersModal } from './components/ReservedOrdersModal';
import type { ReleaseDraft } from './components/ReservedOrdersModal';
import { StockAdjustPopover } from './components/StockAdjustPopover';
import type { StockAdjustConflict } from './components/StockAdjustPopover';
import { StockHistoryPane } from './components/StockHistoryPane';
import { LocationStockCard } from './components/LocationStockCard';
import { exactStockLocation } from './locationStock';
import { historySearchInAddress, inventoryAddress, tabInAddress } from './inventoryAddress';
import type { StockHistoryAddress } from './inventoryAddress';
import { BarcodeCell } from '@/pages/fulfillment/components/BarcodeCell';
import { DownloadIcon, PencilIcon, RefreshIcon, UploadIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { AUTO_DISMISS, useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import {
  closedStockTakeRefusalOf,
  describeClosedStockTake,
  describeFailure,
  describeStaleStock,
  staleStockRefusalOf,
} from '@/utils/fulfillmentErrors';
import {
  type BulkItemReason,
  formatBulkResult,
  UNCONFIRMED_BULK_RESULT,
  interruptedSequenceResult,
} from '@/pages/fulfillment/components/bulkResult';
import { ProductImageZoom } from '@/pages/fulfillment/components/ProductImageZoom';
import '../wireframe.css';
import '../ultimateDesign.css';
import './inventory.css';

const GENERIC_ERROR = 'Could not load the stock. Please try again shortly.';

/** Column widths that keep the whole table inside the viewport; the image column is the one fixed-pixel column (a 32px thumbnail plus the cell's 8px sides). */
const BROWSE_COLUMN_WIDTHS: Array<[string, string]> = [
  ['sku', '10%'],
  ['image', '48px'],
  ['name', '16%'],
  ['name-kr', '14%'],
  ['size', '6%'],
  ['barcode', '12%'],
  ['route', '8%'],
  ['location', '9%'],
  ['total', '8%'],
  ['reserved', '6%'],
  ['available', '6%'],
];
const AUDIT_COLUMN_WIDTHS: Array<[string, string]> = [
  ['sku', '9%'],
  ['image', '48px'],
  ['name', '12%'],
  ['name-kr', '11%'],
  ['size', '5%'],
  ['barcode', '10%'],
  ['route', '7%'],
  ['location', '8%'],
  ['total', '7%'],
  ['reserved', '6%'],
  ['available', '6%'],
  ['counted', '7%'],
  ['diff', '5%'],
];

const BROWSE_LIMIT = 500;
// Match the server's observed page size; 100 pages cover 20,000 including-empty rows.
const LOCATION_STOCK_PAGE_SIZE = 200;
const LOCATION_STOCK_PAGE_CAP = 100;
const LOCATION_STOCK_PROBE_MS = 300;

interface LocationStockRead {
  queryCode: string;
  locationCode: string | null;
  rows: InventoryScreenRow[];
  complete: boolean;
  sourceRows: InventoryScreenRow[];
}

/** How long a typed count sits in the tab before it is written to its round on the server. */
const COUNT_PERSIST_MS = 1000;

const countStateKey = (docId: string, rowKey: string): string => `${docId}\u0000${rowKey}`;

/** How many rounds the audit log reads per page; older ones come on request [SSD-7212]. */
const ROUNDS_PAGE_SIZE = 50;

/** The round a 409 STOCK_TAKE_OPEN names, and who holds it; the server calls the round `docId` everywhere [D-n]. */
interface StandingRound {
  docId: string;
  operator: string | null;
  auditGroupId: string | null;
}

/** The round a shelf already has open, when that is what a 409 is saying [D-n]. */
const standingRoundOf = (failure: unknown): StandingRound | null => {
  const body = isAxiosError<{
    code?: string;
    docId?: string;
    operator?: string | null;
    auditGroupId?: string | null;
  }>(failure)
    ? failure.response?.data
    : undefined;
  return body?.code === 'STOCK_TAKE_OPEN' && body.docId
    ? { docId: body.docId, operator: body.operator ?? null, auditGroupId: body.auditGroupId ?? null }
    : null;
};

/**
 * Whether a round was opened by the person at this keyboard. Once request attribution is available,
 * the server credits a round to that handle and it is the only value that can match; until then, no
 * shared-system round is ours.
 */
const isOwnRound = (operator: string | null | undefined): boolean => {
  const self = OperatorIdentity.current();
  return Boolean(self && operator && operator.trim().toLowerCase() === self);
};

/**
 * The rounds a refresh should pick back up: the operator's own most recent walk and nothing else.
 * Another worker's round is theirs to finish, and a round opened one shelf at a time belongs to the
 * Stock Take screen, so neither is ever folded into this walk. The list comes newest first.
 */
const walkToResume = (rounds: StockTakeRound[]): StockTakeRound[] => {
  const own = rounds.filter((round) => round.status === 'OPEN' && round.auditGroupId && isOwnRound(round.operator));
  const auditGroupId = own[0]?.auditGroupId;
  return auditGroupId ? own.filter((round) => round.auditGroupId === auditGroupId) : [];
};

/**
 * The skus a round froze when it opened, read off the round document the detail endpoint returns.
 * Only a stored array is a frozen set; anything else means the round never recorded one.
 */
const frozenTargetsOf = (detail: StockTakeDetail): string[] | null => {
  const meta = detail.doc?.['meta'] as { targetSkus?: unknown } | undefined;
  return Array.isArray(meta?.targetSkus) && meta.targetSkus.every((sku) => typeof sku === 'string')
    ? (meta.targetSkus as string[])
    : null;
};

/** Why a round closed without applying anything — the server keeps the walk on record either way. */
const ABANDON_REASON = 'stock audit exited';

/** Why a ✎ adjustment's own round was closed after the server refused its count or confirm. */
const ADJUST_REFUSED_REASON = 'stock adjustment refused';

/** One ✎ correction, with the balance its editor opened against so a stale save is refused. */
type AdjustmentEntry = AuditDifference & { expectedSystemQty: number; reason: AdjustReason | null };

/** Carries how many shelves were already confirmed when a later one was refused. */
class PartialAuditFailure extends Error {
  public constructor(
    public readonly confirmed: number,
    public readonly total: number,
    public readonly cause: unknown,
  ) {
    super('The audit was only partly recorded.');
  }
}

/**
 * Renders inventory browsing, audits, reservations, and stock movement forms.
 * Reports operation results through shared fulfillment notifications, keeping
 * failures visible until dismissed while successful notifications leave 6 s after they can be read (SSD-7705).
 */
export default function FulfillmentInventoryPage() {
  const adminClient = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const stockClient = useMemo(() => new StockClient(), []);
  const { user } = useAuth();
  const operator = user?.name || user?.email || 'admin';

  const { toasts, showSuccess, showError, showWarning, showFailure, dismiss } = useFulfillmentToast();

  // The tab and a Stock History search live in the address, so a reload, Back or a link lands on them [SSD-7444].
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabInAddress(searchParams);
  const historySearch = useMemo(() => historySearchInAddress(searchParams), [searchParams]);
  const showInAddress = (nextTab: InventoryTab, search: StockHistoryAddress | null) => {
    const next = inventoryAddress(searchParams, nextTab, search);
    // The same address again would only stack a duplicate entry under Back.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next);
    }
  };
  const [rows, setRows] = useState<InventoryScreenRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [line, setLine] = useState('all');
  const [route, setRoute] = useState('all');
  const [showZeroQty, setShowZeroQty] = useState(false);
  const [locationStockRead, setLocationStockRead] = useState<LocationStockRead | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('available-desc');

  const [locationDrafts, setLocationDrafts] = useState<Record<string, string>>({});
  const [adjusting, setAdjusting] = useState<string | null>(null);
  /** The ✎ box open on the fix-first list. Opening it closes the stock table's and the reverse, so one row never opens two. */
  const [fixing, setFixing] = useState<string | null>(null);
  const [adjustBusy, setAdjustBusy] = useState(false);
  /** The open ✎ box whose save was refused because the shelf moved under it; it stays open with the typed count. */
  const [adjustConflict, setAdjustConflict] = useState<(StockAdjustConflict & { key: string }) | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  /** Bumped by Refresh, so the order a ✎ save held is let go [SSD-7449]. */
  const [sortEpoch, setSortEpoch] = useState(0);
  /** The order on screen when a ✎ save landed, the view it was shown for and the row saved [SSD-7449]. */
  const [heldOrder, setHeldOrder] = useState<{ view: string; keys: string[]; key: string } | null>(null);
  /** The row order of the last committed render, recorded after commit so a discarded render never counts. */
  const committedOrderRef = useRef<string[]>([]);

  const [auditOn, setAuditOn] = useState(false);
  const [auditRowsSource, setAuditRowsSource] = useState<InventoryScreenRow[] | null>(null);
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [confirmingAudit, setConfirmingAudit] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  const confirmingAuditRef = useRef(false);
  const [signedOff, setSignedOff] = useState(false);
  /** Ties every shelf of one walk together, so a refresh finds the whole session rather than one round. */
  const [auditGroupId, setAuditGroupId] = useState(() => uuidv4());
  const [exiting, setExiting] = useState(false);
  // locationCode → the open round the server holds for that shelf; the walk is the server's, not the tab's [D-n].
  const [openRoundsByShelf, setOpenRoundsByShelf] = useState<Record<string, string>>({});
  const [auditTargetsByShelf, setAuditTargetsByShelf] = useState<Record<string, string[]>>({});
  const [rounds, setRounds] = useState<StockTakeRound[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [showRounds, setShowRounds] = useState(false);
  /** Where the next page of the audit log starts; null once it has been read to its end. */
  const [roundsCursor, setRoundsCursor] = useState<string | null>(null);
  /** Bumped when the audit log starts a new listing, so a page that answers for an older one is ignored. */
  const roundsListingRef = useRef(0);
  const [roundsOrigin, setRoundsOrigin] = useState<StockTakeOrigin | 'all'>('all');
  const [importingLocations, setImportingLocations] = useState(false);

  const [reservedSku, setReservedSku] = useState<string | null>(null);
  const [reservedLocation, setReservedLocation] = useState('');
  const [holders, setHolders] = useState<ReservationHolder[]>([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [holdersFailed, setHoldersFailed] = useState(false);
  const [releaseDraft, setReleaseDraft] = useState<ReleaseDraft>({ reason: '', restock: false, restockQty: '' });
  const [releaseBusy, setReleaseBusy] = useState<string | null>(null);
  const relocatingRef = useRef(new Set<string>());
  const releaseInFlightRef = useRef(false);
  const exportInFlightRef = useRef(false);
  const adjustInFlightRef = useRef(false);
  const shelfKeysRef = useRef(new Map<string, string>());
  const reservedRequestRef = useRef(0);
  const reservedSkuRef = useRef<string | null>(null);
  const loadRequestRef = useRef(0);
  const locationStockRequestRef = useRef(0);
  const recoveryRequestRef = useRef(0);
  /** What the server was last told for each counted line, so a flush never re-sends an unchanged count. */
  const persistedRef = useRef<Record<string, string>>({});
  const savedCountsRef = useRef<Record<string, string>>({});
  const countedRef = useRef<Record<string, string>>({});
  const countWriteQueuesRef = useRef<Record<string, Promise<void>>>({});
  const scheduledCountsRef = useRef<Record<string, string>>({});
  const countSaveFailuresRef = useRef<Record<string, unknown>>({});
  /** Rows whose count or confirm was refused because their shelf moved; each is recounted before the next confirm. */
  const [staleRecounts, setStaleRecounts] = useState<Record<string, StaleStockLine>>({});
  /**
   * Rounds this walk no longer writes to: confirmed here, or closed by a colleague [SSD-7209]. Read synchronously,
   * so a second refused write on the same shelf says nothing new, a queued write is not sent to it, and two shelves
   * refused in the same flush each see the other one gone.
   */
  const closedRoundsRef = useRef<Set<string>>(new Set());
  /** The server's reason for each round a colleague closed under this walk, for Confirm's outcome to name it. */
  const closedRefusalsRef = useRef<Record<string, ClosedStockTakeRefusal>>({});
  /** Stale holds a typed number lifted; a box emptied again before that count is saved puts its hold back. */
  const liftedStaleRef = useRef<Record<string, StaleStockLine>>({});
  /** The book value each untouched line was prefilled with; it follows the book until the floor enters a count. */
  const prefilledRef = useRef<Record<string, string>>({});
  /** Read by `load`, which runs from listeners that would otherwise see a stale `auditOn`. */
  const auditOnRef = useRef(false);
  auditOnRef.current = auditOn;
  const countInputsRef = useRef<Array<HTMLInputElement | null>>([]);
  countedRef.current = counted;

  // A refusal belongs to the box it was shown in; opening or closing any box starts clean.
  useEffect(() => {
    setAdjustConflict(null);
  }, [adjusting, fixing]);

  /** A read already in flight predates a local patch, so it must not land over it. */
  const invalidateInventoryReads = () => {
    loadRequestRef.current += 1;
    setLoading(false);
  };

  /**
   * Answers the rows it just loaded, so a caller can decide whether its own filter still points anywhere;
   * null when a newer read or a local patch has taken the screen since.
   */
  const load = async (includeEmpty: boolean, saved?: string): Promise<InventoryScreenRow[] | null> => {
    const request = ++loadRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await adminClient.listInventory({
        limit: BROWSE_LIMIT,
        // A frozen target can sit at zero, and hiding it mid-walk would drop it from the audit.
        ...(includeEmpty || auditOnRef.current ? { includeEmpty: true } : {}),
      });
      // A newer read owns the screen now; this one still answered.
      if (request !== loadRequestRef.current) {
        if (saved) {
          showSuccess(saved);
        }
        return null;
      }
      setRows(result.rows);
      setLocationDrafts({});
      const skus = [...new Set(result.rows.map((row) => row.sku))];
      if (skus.length > 0) {
        // The stock endpoint carries quantities only; name, size, barcode and image come from the catalog.
        // It has to be the admin lookup: the customer-facing one sanitizes nameKor and the barcode away.
        try {
          const products = await stockClient.getAdminProductsBySkus(skus);
          if (request === loadRequestRef.current) {
            setRows(mergeProductInfo(result.rows, products));
          }
        } catch {
          // A catalog outage must not blank the quantities the warehouse came here for.
          if (request === loadRequestRef.current) {
            showError('Product names are unavailable right now.');
          }
        }
      }
      if (saved) {
        showSuccess(saved);
      }
      return result.rows;
    } catch {
      if (request !== loadRequestRef.current) {
        if (saved) {
          showSuccess(saved);
        }
        return null;
      }
      if (saved) {
        showWarning(`${saved} Could not refresh inventory; reload before taking another action.`);
      }
      setError(GENERIC_ERROR);
      return [];
    } finally {
      if (request === loadRequestRef.current) {
        setLoading(false);
      }
    }
  };

  /**
   * A barcode typed here is saved against the product master, not this shelf — the same gap follows
   * the sku onto every screen, so closing it once closes it everywhere. A refusal leaves the input up.
   */
  const saveBarcode = async (productId: number, barcode: string): Promise<BarcodeOwner | undefined> => {
    const result = await productAttributesClient.updateBarcode(productId, barcode);
    if (!result.success) {
      // Another product carries the code: the cell shows which one under the box (SSD-7532).
      if (result.owner) {
        return result.owner;
      }
      showError(result.message);
      return undefined;
    }
    invalidateInventoryReads();
    setRows((previous) => previous.map((row) => (row.productId === productId ? { ...row, barcode } : row)));
    showSuccess(`✓ Barcode saved — ${barcode}`);
    return undefined;
  };

  // The server withholds rows at zero, so revealing them is a new request, not a filter over what we hold.
  useEffect(() => {
    void load(showZeroQty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showZeroQty]);

  // Another desk moves stock all day, so a tab coming back to the front is looking at a stale book [D-p].
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void load(showZeroQty);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showZeroQty]);

  // A walk lives on the server, so a refresh picks the standing rounds and their counts back up [D-n].
  useEffect(() => {
    const recoveryRequest = ++recoveryRequestRef.current;
    void (async () => {
      try {
        const result = await adminClient.listStockTakes({ status: 'OPEN', limit: 100 });
        const open = walkToResume(result.rounds);
        if (open.length === 0) {
          return;
        }
        const shelves: Record<string, string> = {};
        const resumed: Record<string, string> = {};
        const persisted: Record<string, string> = {};
        const saved: Record<string, string> = {};
        const targets: Record<string, string[]> = {};
        for (const round of open) {
          shelves[round.locationCode] = round.docId;
          const detail = await adminClient.getStockTake(round.docId);
          targets[round.locationCode] =
            round.targetSkus ?? frozenTargetsOf(detail) ?? detail.lines.map((line) => line.sku);
          detail.lines.forEach((line) => {
            if (line.countedQty !== null && line.countedQty !== undefined) {
              const key = `${line.sku}@${round.locationCode}`;
              const value = String(line.countedQty);
              resumed[key] = value;
              persisted[countStateKey(round.docId, key)] = value;
              saved[countStateKey(round.docId, key)] = value;
            }
          });
        }
        if (recoveryRequest !== recoveryRequestRef.current) {
          return;
        }
        persistedRef.current = persisted;
        scheduledCountsRef.current = { ...persisted };
        savedCountsRef.current = saved;
        setCounted(resumed);
        // Round ids are not reused, but a round this tab holds open is never one it stopped writing to.
        Object.values(shelves).forEach((docId) => closedRoundsRef.current.delete(docId));
        setOpenRoundsByShelf(shelves);
        setAuditTargetsByShelf(targets);
        setAuditGroupId(open[0]?.auditGroupId ?? uuidv4());
        setAuditOn(true);
      } catch {
        // A pool that cannot be read is not a walk that is not standing; the desk can start one by hand.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickabilityUnreported = rows.some((row) => row.pickable === undefined);
  // After a ✎ save the table holds its order, so the row that changed stays under the operator's eye; choosing a sort,
  // a filter, the zero-row toggle, the audit or Refresh lets the rows fall into order again, and so does the saved row
  // leaving the book [SSD-7449].
  const orderView = [sortMode, query, line, route, showZeroQty, auditOn, sortEpoch].join('\u0000');
  const holdingOrder =
    heldOrder !== null && heldOrder.view === orderView && rows.some((row) => keyOf(row) === heldOrder.key);
  // Nothing sweeps emptied rows away any more, so they pile up; a negative is a defect and always stays. A row just
  // counted down to 0 stays too while the order is held, or the new count would vanish before it is read.
  const stockQueryCode = searchedCode(query);
  const browseStockLocation = exactStockLocation(rows, query);
  const readForCurrentRows =
    locationStockRead?.queryCode === stockQueryCode && locationStockRead.sourceRows === rows ? locationStockRead : null;
  // Master has no scanned-shelf search: a whole known code takes precedence over SKU/name matching.
  const stockLocation = readForCurrentRows?.locationCode ?? browseStockLocation;
  const tableRows = readForCurrentRows?.locationCode ? readForCurrentRows.rows : rows;
  const withStock = readForCurrentRows?.locationCode
    ? tableRows
    : showZeroQty
      ? tableRows
      : tableRows.filter((row) => row.onHand !== 0 || (holdingOrder && keyOf(row) === heldOrder?.key));
  const hiddenZeroQty = tableRows.length - withStock.length;
  const shortSkus = oversoldSkus(rows);
  const sortedRows = sortRows(
    withStock.filter(
      (row) =>
        (stockLocation !== null ? row.locationCode === stockLocation : matchesQuery(row, query)) &&
        inLine(row, line) &&
        inRoute(row, route),
    ),
    auditOn ? 'location-asc' : sortMode,
  );
  const visible = holdingOrder && heldOrder ? keepOrder(sortedRows, heldOrder.keys) : sortedRows;
  // A save holds the order the operator was looking at, the last committed one. It runs before that order is recorded
  // again, and as a layout effect, so the re-sorted rows never reach the screen.
  useLayoutEffect(() => {
    if (savedKey && !auditOn) {
      setHeldOrder({ view: orderView, keys: committedOrderRef.current, key: savedKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);
  useLayoutEffect(() => {
    committedOrderRef.current = visible.map(keyOf);
  });
  // Let go for good once the view changes or the saved row is gone, so sorting back to the same view does not revive it.
  useLayoutEffect(() => {
    if (heldOrder && !holdingOrder) {
      setHeldOrder(null);
    }
  }, [heldOrder, holdingOrder]);
  // Memoized on the session alone, so a re-render (a toast, a keystroke) does not look like a new walk to auto-save.
  const sessionRows = useMemo(() => {
    const latestRows = new Map(rows.map((row) => [keyOf(row), row]));
    return sortRows(
      (auditRowsSource ?? rows)
        .filter((row) => {
          const targets = auditTargetsByShelf[row.locationCode];
          return Boolean(openRoundsByShelf[row.locationCode] && targets?.includes(row.sku));
        })
        .map((row) => latestRows.get(keyOf(row)) ?? row),
      'location-asc',
    );
  }, [auditRowsSource, auditTargetsByShelf, openRoundsByShelf, rows]);
  const auditRows = auditOn ? sessionRows : visible;
  const requestedStockLocation = stockLocation ?? (isLocationCode(stockQueryCode) ? stockQueryCode : null);
  const cardLocation = readForCurrentRows ? readForCurrentRows.locationCode : stockLocation;
  const cardRows = readForCurrentRows?.locationCode
    ? readForCurrentRows.rows.filter((row) => inLine(row, line) && inRoute(row, route))
    : visible;
  const cardComplete = readForCurrentRows?.locationCode ? readForCurrentRows.complete : false;
  useEffect(() => {
    const request = ++locationStockRequestRef.current;
    if (auditOn || requestedStockLocation === null) {
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      void (async () => {
        const browseLocationRows = new Map(
          rows
            .filter((row) => normalizeLocationCode(row.locationCode) === stockQueryCode)
            .map((row) => [keyOf(row), row]),
        );
        const readLocationRows = new Map<string, InventoryScreenRow>();
        const seenCursors = new Set<string>();
        let cursor: string | undefined;
        let complete = false;
        try {
          for (let page = 0; page < LOCATION_STOCK_PAGE_CAP; page += 1) {
            const result = await adminClient.listInventory({
              limit: LOCATION_STOCK_PAGE_SIZE,
              includeEmpty: true,
              ...(cursor ? { cursor } : {}),
            });
            if (!active || request !== locationStockRequestRef.current) {
              return;
            }
            result.rows.forEach((row) => {
              if (normalizeLocationCode(row.locationCode) === stockQueryCode) {
                readLocationRows.set(keyOf(row), row);
              }
            });
            // An older response can omit the cursor; a full page then does not prove exhaustion.
            if (result.nextCursor === undefined && result.rows.length >= LOCATION_STOCK_PAGE_SIZE) {
              break;
            }
            const nextCursor = result.nextCursor ?? null;
            if (nextCursor === null) {
              complete = true;
              break;
            }
            if (seenCursors.has(nextCursor)) {
              break;
            }
            seenCursors.add(nextCursor);
            cursor = nextCursor;
          }
        } catch {
          complete = false;
        }
        if (!active || request !== locationStockRequestRef.current) {
          return;
        }
        let locationRows = [
          ...(complete ? readLocationRows.values() : new Map([...browseLocationRows, ...readLocationRows]).values()),
        ].map((row) => ({ ...rows.find((entry) => keyOf(entry) === keyOf(row)), ...row }));
        const skus = [...new Set(locationRows.map((row) => row.sku))];
        if (skus.length > 0) {
          try {
            const products = await stockClient.getAdminProductsBySkus(skus);
            if (!active || request !== locationStockRequestRef.current) {
              return;
            }
            locationRows = mergeProductInfo(locationRows, products);
          } catch {
            if (!active || request !== locationStockRequestRef.current) {
              return;
            }
          }
        }
        setLocationStockRead({
          queryCode: stockQueryCode,
          locationCode: locationRows[0]?.locationCode ?? (complete ? null : requestedStockLocation),
          rows: locationRows,
          complete,
          sourceRows: rows,
        });
      })();
    }, LOCATION_STOCK_PROBE_MS);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [adminClient, auditOn, requestedStockLocation, rows, stockClient, stockQueryCode]);
  // A book that does not add up is left out of the walk rather than stopping it, so it is listed instead [SSD-6932].
  const needsFixing = sortRows(
    rows.filter((row) => isOversold(row) && !(auditOn && sessionRows.includes(row))),
    'location-asc',
  );

  const hasActiveFilters = rows.length > 0 && (query.trim() !== '' || line !== 'all' || route !== 'all');
  const clearFilters = () => {
    setQuery('');
    setLine('all');
    setRoute('all');
    setShowZeroQty(false);
  };

  const differences: AuditDifference[] = auditRows
    .map((row) => ({ row, diff: countedDiff(row.onHand, counted[keyOf(row)] ?? '') }))
    .filter((entry): entry is { row: InventoryScreenRow; diff: number } => entry.diff !== null && entry.diff !== 0)
    .map(({ row, diff }) => ({ row, diff, countedQty: row.onHand + diff }));
  const tally = auditTally(differences.map((entry) => ({ diff: entry.diff, isNewAddition: false })));
  // A count under the reservation leaves holding orders short, and the figure is what makes that actionable.
  const shortfalls: AuditShortfall[] = differences
    .filter((entry) => entry.countedQty < entry.row.reserved)
    .map((entry) => ({
      sku: entry.row.sku,
      locationCode: entry.row.locationCode,
      reserved: entry.row.reserved,
      countedQty: entry.countedQty,
      shortfall: entry.row.reserved - entry.countedQty,
    }));
  const hasInvalidCounts = auditRows.some((row) => countInputError(counted[keyOf(row)] ?? '') !== null);
  // A row whose shelf moved is held until a new count is entered against the stock it holds now [SSD-7209].
  const hasStaleRecounts = Object.keys(staleRecounts).length > 0;
  const hasClearedSavedCounts = auditRows.some((row) => {
    const docId = openRoundsByShelf[row.locationCode];
    if (!docId || counted[keyOf(row)]?.trim()) {
      return false;
    }
    const stateKey = countStateKey(docId, keyOf(row));
    const saved = savedCountsRef.current[stateKey] !== undefined;
    const pending = Boolean(countWriteQueuesRef.current[stateKey]);
    const unsent = scheduledCountsRef.current[stateKey] !== persistedRef.current[stateKey];
    return saved || pending || unsent;
  });

  /** One ADJUST correction is one single-line round, so a refused shelf leaves the confirmed ones confirmed. */
  const recordCounts = async (entries: AdjustmentEntry[]): Promise<{ confirmed: number; total: number }> => {
    const byLocation = new Map<string, AdjustmentEntry[]>();
    entries.forEach((entry) => {
      const at = entry.row.locationCode;
      byLocation.set(at, [...(byLocation.get(at) ?? []), entry]);
    });
    let confirmed = 0;
    for (const [locationCode, lines] of byLocation) {
      const key = shelfKeysRef.current.get(locationCode) ?? uuidv4();
      shelfKeysRef.current.set(locationCode, key);
      let openedDocId: string | null = null;
      try {
        // The origin and each line's reason ride to the ledger, so Stock History can tell this from an audit.
        const { docId } = await adminClient.openStockTake({
          locationCode,
          idempotencyKey: key,
          origin: 'MANUAL_ADJUST',
        });
        openedDocId = docId;
        for (const entry of lines) {
          await adminClient.countStockTake(docId, {
            sku: entry.row.sku,
            countedQty: entry.countedQty,
            expectedSystemQty: entry.expectedSystemQty,
            ...(entry.reason ? { reason: entry.reason } : {}),
          });
        }
        await adminClient.confirmStockTake(docId);
      } catch (failure) {
        // An open round locks its shelf, so a refused count or confirm closes the round this call opened.
        // A round that was never ours to open (a 409 naming another walk) is left alone.
        if (openedDocId) {
          try {
            await adminClient.cancelStockTake(openedDocId, ADJUST_REFUSED_REASON);
            shelfKeysRef.current.delete(locationCode);
          } catch {
            // The server's refusal is the error worth showing; the keyed retry reuses the round if it is still open.
          }
        }
        throw new PartialAuditFailure(confirmed, byLocation.size, failure);
      }
      confirmed += 1;
      shelfKeysRef.current.delete(locationCode);
    }
    return { confirmed, total: byLocation.size };
  };

  const applyAdjustment = async (
    row: InventoryScreenRow,
    countedQty: number,
    reason: AdjustReason | null,
    expectedOnHand: number,
    fromFixList = false,
  ) => {
    if (adjustInFlightRef.current) {
      return;
    }
    // A shelf carries one open round, so a ✎ on a shelf the walk holds, or is still opening, would land in the walk's own round.
    if (auditBusy || openRoundsByShelf[row.locationCode]) {
      setAdjusting(null);
      setFixing(null);
      showError(
        `Shelf ${shelfLabel(row.locationCode)} is open in the stock audit — adjust it once the audit is confirmed or exited.`,
      );
      return;
    }
    adjustInFlightRef.current = true;
    setAdjustBusy(true);
    try {
      const diff = countedQty - row.onHand;
      await recordCounts([
        { row, countedQty, diff, expectedSystemQty: expectedOnHand, reason: adjustReasonFor(diff, reason) },
      ]);
      invalidateInventoryReads();
      setAdjusting(null);
      setFixing(null);
      // Applying gives in-place feedback, not a toast [G-2 CP-9]; only a failure shouts.
      setSavedKey(keyOf(row));
      setRows((previous) =>
        previous.map((entry) => (keyOf(entry) === keyOf(row) ? withOnHand(entry, countedQty) : entry)),
      );
      if (fromFixList && !isOversold(withOnHand(row, countedQty))) {
        // The one exception: a repaired row leaves the fix-first list and takes its in-place marker with it.
        showSuccess(`✓ ${row.sku} adds up again — the next stock audit counts it.`);
      }
    } catch (failure) {
      // recordCounts wraps the wire error, so the server's reason sits one level down.
      const cause = failure instanceof PartialAuditFailure ? failure.cause : failure;
      const moved = staleStockRefusalOf(cause)?.staleLines.find((stale) => stale.sku === row.sku);
      if (moved) {
        // Someone changed this shelf after the box opened: nothing was applied, so the row is redrawn from the
        // balance it holds now and the box clears, so the next number is typed against that balance [SSD-7209].
        const message = `${describeStaleStock([moved])} Enter the quantity again.`;
        setRows((previous) =>
          previous.map((entry) => (keyOf(entry) === keyOf(row) ? withOnHand(entry, moved.current) : entry)),
        );
        setAdjustConflict({ key: keyOf(row), message, current: moved.current });
        showError(message);
        return;
      }
      // The editor closes on a refusal: leaving the typed number up reads as a value the book now holds.
      setAdjusting(null);
      setFixing(null);
      showFailure(cause, 'The adjustment was refused.');
    } finally {
      adjustInFlightRef.current = false;
      setAdjustBusy(false);
    }
  };

  /** The counts a standing round already holds, keyed the way the screen keys its rows. */
  const countsOf = async (
    docId: string,
    locationCode: string,
    localTargets: string[],
  ): Promise<{ counts: Record<string, string>; targetSkus: string[] }> => {
    const counts: Record<string, string> = {};
    const detail = await adminClient.getStockTake(docId);
    detail.lines.forEach((line) => {
      if (line.countedQty !== null && line.countedQty !== undefined) {
        counts[`${line.sku}@${locationCode}`] = String(line.countedQty);
      }
    });
    // The set the round froze is the walk; without one, the lines only name what was counted so far.
    const frozen = frozenTargetsOf(detail);
    return {
      counts,
      targetSkus: frozen ?? [...new Set([...localTargets, ...detail.lines.map((line) => line.sku)])],
    };
  };

  /**
   * Redraws the rows a refusal named from the balance their shelf holds now, clears what was typed for them and
   * forgets what the round holds for them, so the old number is never re-sent against the new book: the row is
   * counted again from scratch, and Confirm stays off until it is [SSD-7209, decision D6].
   */
  const markStaleRecounts = useCallback((docId: string, locationCode: string, lines: StaleStockLine[]) => {
    const marked = Object.fromEntries(lines.map((line) => [keyOf({ sku: line.sku, locationCode }), line]));
    Object.keys(marked).forEach((key) => {
      const stateKey = countStateKey(docId, key);
      delete liftedStaleRef.current[key];
      delete persistedRef.current[stateKey];
      delete scheduledCountsRef.current[stateKey];
      delete savedCountsRef.current[stateKey];
      delete countSaveFailuresRef.current[stateKey];
      delete prefilledRef.current[stateKey];
    });
    setRows((previous) =>
      previous.map((entry) => {
        const moved = marked[keyOf(entry)];
        return moved ? withOnHand(entry, moved.current) : entry;
      }),
    );
    setCounted((entries) => ({ ...entries, ...Object.fromEntries(Object.keys(marked).map((key) => [key, ''])) }));
    setStaleRecounts((standing) => ({ ...standing, ...marked }));
  }, []);

  /** Set once the walk's close-out helpers exist below; a count write that lands on a closed round calls through it. */
  const closedRoundRefusedRef = useRef<(locationCode: string, refusal: ClosedStockTakeRefusal, docId: string) => void>(
    () => undefined,
  );

  /**
   * Writes one counted line to its round, and remembers it so the next flush leaves it alone. The count is made
   * against the book the row shows, so a shelf that moved since is refused rather than adjusted by a stale figure.
   */
  const persistCount = useCallback(
    async (docId: string, row: InventoryScreenRow, text: string) => {
      const key = keyOf(row);
      const { sku, onHand: expectedSystemQty } = row;
      const value = text.trim();
      if (!isValidCount(value)) {
        return Promise.resolve();
      }
      const stateKey = countStateKey(docId, key);
      const queued = countWriteQueuesRef.current[stateKey];
      if (persistedRef.current[stateKey] === value && scheduledCountsRef.current[stateKey] === value) {
        return queued ?? Promise.resolve();
      }
      if (scheduledCountsRef.current[stateKey] === value && queued) {
        return queued;
      }
      scheduledCountsRef.current[stateKey] = value;
      const write = (queued ?? Promise.resolve())
        .catch(() => undefined)
        .then(async () => {
          if (persistedRef.current[stateKey] === value) {
            return;
          }
          if (closedRoundsRef.current.has(docId)) {
            // A colleague closed this round while the write waited: it is no longer ours to send to.
            delete scheduledCountsRef.current[stateKey];
            return;
          }
          try {
            await adminClient.countStockTake(docId, { sku, countedQty: Number(value), expectedSystemQty });
            persistedRef.current[stateKey] = value;
            savedCountsRef.current[stateKey] = value;
            // The recount is saved: an emptied box no longer brings the stale note back, and one put back meanwhile goes.
            delete liftedStaleRef.current[key];
            setStaleRecounts((standing) => {
              if (!standing[key]) {
                return standing;
              }
              const rest = { ...standing };
              delete rest[key];
              return rest;
            });
            if (scheduledCountsRef.current[stateKey] === value) {
              delete countSaveFailuresRef.current[stateKey];
            }
          } catch (failure) {
            if (scheduledCountsRef.current[stateKey] === value) {
              countSaveFailuresRef.current[stateKey] = failure;
            }
            // The shelf moved since the row was read, so nothing was saved: the row is cleared for a new count.
            const moved = staleStockRefusalOf(failure)?.staleLines.filter((line) => line.sku === sku) ?? [];
            const closed = closedStockTakeRefusalOf(failure);
            if (moved.length > 0) {
              markStaleRecounts(docId, row.locationCode, moved);
              showError(`${describeStaleStock(moved)} Enter a new count for it, then confirm.`);
            } else if (closed) {
              // The round is gone, not a save to retry: Confirm must not report it as a count that failed.
              delete countSaveFailuresRef.current[stateKey];
              // A colleague sealed or abandoned this shelf's round: it leaves the walk and the shelf is read again.
              closedRoundRefusedRef.current(row.locationCode, closed, docId);
              // Said and handled: a flush waiting on this write must go on to the shelves still open [#1125 review].
              return;
            } else {
              showFailure(failure, 'The count could not be saved.');
            }
            throw failure;
          }
        });
      countWriteQueuesRef.current[stateKey] = write;
      void write.then(
        () => {
          if (countWriteQueuesRef.current[stateKey] === write) {
            delete countWriteQueuesRef.current[stateKey];
          }
        },
        () => {
          if (countWriteQueuesRef.current[stateKey] === write) {
            delete countWriteQueuesRef.current[stateKey];
          }
        },
      );
      return write;
    },
    [adminClient, markStaleRecounts, showError, showFailure],
  );

  /** Every counted line the server has not been told about yet — the walk must survive a closed tab. */
  const flushCounts = async (): Promise<void> => {
    if (auditRows.some((row) => countInputError(countedRef.current[keyOf(row)] ?? '') !== null)) {
      throw new Error(COUNT_ERROR);
    }
    const activeDocIds = new Set(
      Object.values(openRoundsByShelf).filter((docId) => !closedRoundsRef.current.has(docId)),
    );
    await Promise.all(
      Object.entries(countWriteQueuesRef.current)
        .filter(([key]) => activeDocIds.has(key.split('\u0000', 1)[0]!))
        .map(([, write]) => write),
    );
    if (
      auditRows.some((row) => {
        const docId = openRoundsByShelf[row.locationCode];
        if (!docId || closedRoundsRef.current.has(docId) || countedRef.current[keyOf(row)]?.trim()) {
          return false;
        }
        const stateKey = countStateKey(docId, keyOf(row));
        const saved = savedCountsRef.current[stateKey] !== undefined;
        const unsent = scheduledCountsRef.current[stateKey] !== persistedRef.current[stateKey];
        return saved || unsent;
      })
    ) {
      throw new Error('A saved count cannot be cleared.');
    }
    await Promise.all(
      auditRows.map((row) => {
        const docId = openRoundsByShelf[row.locationCode];
        if (docId && !closedRoundsRef.current.has(docId)) {
          return persistCount(docId, row, countedRef.current[keyOf(row)] ?? '');
        }
        return Promise.resolve();
      }),
    );
    if (
      Object.keys(countSaveFailuresRef.current).some((key) => {
        const docId = key.split('\u0000', 1)[0]!;
        return activeDocIds.has(docId) && !closedRoundsRef.current.has(docId);
      })
    ) {
      throw new Error('The count could not be saved.');
    }
  };

  // An untouched line stands for "matches the book", so a reloaded book moves it too; nothing was sent for it [D-n].
  useEffect(() => {
    if (!auditOn) {
      return;
    }
    const moved: Record<string, string> = {};
    for (const row of sessionRows) {
      const docId = openRoundsByShelf[row.locationCode];
      if (!docId) {
        continue;
      }
      const stateKey = countStateKey(docId, keyOf(row));
      const prefilled = prefilledRef.current[stateKey];
      const book = String(row.onHand);
      if (
        prefilled === undefined ||
        prefilled === book ||
        savedCountsRef.current[stateKey] !== undefined ||
        countedRef.current[keyOf(row)] !== prefilled
      ) {
        continue;
      }
      prefilledRef.current[stateKey] = book;
      persistedRef.current[stateKey] = book;
      scheduledCountsRef.current[stateKey] = book;
      moved[keyOf(row)] = book;
    }
    if (Object.keys(moved).length > 0) {
      setCounted((entries) => ({ ...entries, ...moved }));
    }
  }, [auditOn, openRoundsByShelf, sessionRows]);

  // A count typed at the desk belongs to its round on the server, not to this tab [D-n].
  useEffect(() => {
    if (!auditOn) {
      return;
    }
    const timer = setTimeout(() => {
      void (async () => {
        for (const row of auditRows) {
          const docId = openRoundsByShelf[row.locationCode];
          if (!docId) {
            continue;
          }
          const stateKey = countStateKey(docId, keyOf(row));
          const value = (counted[keyOf(row)] ?? '').trim();
          // A value the server just refused is retried by Enter or Confirm, not re-sent on every render.
          if (countSaveFailuresRef.current[stateKey] !== undefined && scheduledCountsRef.current[stateKey] === value) {
            continue;
          }
          await persistCount(docId, row, counted[keyOf(row)] ?? '').catch(() => undefined);
        }
      })();
    }, COUNT_PERSIST_MS);
    return () => clearTimeout(timer);
  }, [auditOn, auditRows, counted, openRoundsByShelf, persistCount]);

  /** One round per shelf, freezing its skus: widening the filter mid-walk must not grow what was opened [D-n]. */
  const startAudit = async () => {
    if (visible.length === 0) {
      return;
    }
    // Counting a row whose book does not add up would seal a figure already known to be wrong, so it sits this walk out.
    const countable = visible.filter((row) => !isOversold(row));
    if (countable.length === 0) {
      showWarning(
        `There is nothing to count — ${stockIntegritySummary(visible, true)}. Repair them under "Needs fixing first", then start the audit.`,
        undefined,
        AUTO_DISMISS,
      );
      return;
    }
    // A ✎ box left open would apply on a shelf this walk is about to hold.
    setAdjusting(null);
    setFixing(null);
    recoveryRequestRef.current += 1;
    const shelves = new Map<string, string[]>();
    // Rounds open in walk order, so the shelf a refusal names is the one the floor was about to stand at.
    sortRows(countable, 'location-asc').forEach((row) =>
      shelves.set(row.locationCode, [...(shelves.get(row.locationCode) ?? []), row.sku]),
    );
    const opened: Record<string, string> = {};
    const targets: Record<string, string[]> = {};
    /** Shelves whose round was already standing, so their counts come from the server rather than the book. */
    const joined: string[] = [];
    setAuditBusy(true);
    try {
      for (const [locationCode, targetSkus] of shelves) {
        const idempotencyKey = shelfKeysRef.current.get(locationCode) ?? uuidv4();
        shelfKeysRef.current.set(locationCode, idempotencyKey);
        try {
          const { docId } = await adminClient.openStockTake({
            locationCode,
            idempotencyKey,
            auditGroupId,
            targetSkus,
          });
          opened[locationCode] = docId;
          targets[locationCode] = targetSkus;
        } catch (failure) {
          // The shelf already has our own round open; joining it is the point of a server session [D-n].
          const standing = standingRoundOf(failure);
          if (standing && standing.auditGroupId === auditGroupId && isOwnRound(standing.operator)) {
            opened[locationCode] = standing.docId;
            targets[locationCode] = targetSkus;
            joined.push(locationCode);
            continue;
          }
          // Half a walk is worse than none: the rounds this walk opened are closed again before reporting.
          // A joined round belongs to the walk that opened it, so it stays open with its counts as found.
          for (const [shelf, docId] of Object.entries(opened)) {
            if (!joined.includes(shelf)) {
              try {
                await adminClient.cancelStockTake(docId, ABANDON_REASON);
                shelfKeysRef.current.delete(shelf);
              } catch {
                // A failed rollback leaves the round open and its key available for retry.
              }
            }
          }
          if (standing) {
            // Someone else is counting this shelf; their round is theirs to finish, never ours to merge in.
            showError(
              `Shelf ${locationCode} is being counted by ${standing.operator ?? 'another worker'} — the audit was not started.`,
            );
          } else {
            showFailure(failure, `Shelf ${locationCode} would not open — the audit was not started.`);
          }
          return;
        }
      }
      const prefill: Record<string, string> = {};
      const saved: Record<string, string> = {};
      // A round we joined rather than opened already holds counts; those outrank the book value.
      for (const locationCode of joined) {
        let contents: { counts: Record<string, string>; targetSkus: string[] };
        try {
          contents = await countsOf(opened[locationCode] ?? '', locationCode, targets[locationCode] ?? []);
        } catch (failure) {
          for (const [shelf, docId] of Object.entries(opened)) {
            if (!joined.includes(shelf)) {
              try {
                await adminClient.cancelStockTake(docId, ABANDON_REASON);
                shelfKeysRef.current.delete(shelf);
              } catch {
                // A failed rollback leaves the round open and its key available for retry.
              }
            }
          }
          showFailure(failure, `Shelf ${locationCode} could not be read — the audit was not started.`);
          return;
        }
        targets[locationCode] = contents.targetSkus;
        Object.assign(prefill, contents.counts);
        Object.entries(contents.counts).forEach(([key, value]) => {
          saved[countStateKey(opened[locationCode] ?? '', key)] = value;
        });
      }
      // A round that was already open can have frozen more SKUs than the filtered rows that joined it.
      const walkCandidates = new Map([...rows, ...visible].map((row) => [keyOf(row), row]));
      const rowsInWalk = [...walkCandidates.values()].filter((row) => targets[row.locationCode]?.includes(row.sku));
      rowsInWalk.forEach((row) => {
        if (prefill[keyOf(row)] === undefined) {
          prefill[keyOf(row)] = String(row.onHand);
        }
      });
      // The book value counts as already told to the server: a matching shelf is one keystroke, not one request.
      const persisted = Object.fromEntries(
        rowsInWalk.flatMap((row) => {
          const docId = opened[row.locationCode];
          const value = prefill[keyOf(row)];
          return docId && value !== undefined ? [[countStateKey(docId, keyOf(row)), value]] : [];
        }),
      );
      persistedRef.current = persisted;
      scheduledCountsRef.current = { ...persisted };
      savedCountsRef.current = saved;
      prefilledRef.current = Object.fromEntries(
        Object.entries(persisted).filter(([stateKey]) => saved[stateKey] === undefined),
      );
      setCounted(prefill);
      setAuditRowsSource(rowsInWalk);
      Object.values(opened).forEach((docId) => closedRoundsRef.current.delete(docId));
      setOpenRoundsByShelf(opened);
      setAuditTargetsByShelf(targets);
      setAuditOn(true);
    } finally {
      setAuditBusy(false);
    }
  };

  const leaveAudit = () => {
    // closedRoundsRef is kept: a refused write still in flight for a round of this walk must stay quiet.
    liftedStaleRef.current = {};
    shelfKeysRef.current.clear();
    persistedRef.current = {};
    savedCountsRef.current = {};
    scheduledCountsRef.current = {};
    countSaveFailuresRef.current = {};
    prefilledRef.current = {};
    setStaleRecounts({});
    setCounted({});
    setAuditRowsSource(null);
    setOpenRoundsByShelf({});
    setAuditTargetsByShelf({});
    setAuditGroupId(uuidv4());
    setAuditOn(false);
    setSignedOff(false);
  };

  /** Takes one shelf out of the walk: its round is confirmed, or closed by someone else, either way no longer ours to send. */
  const dropShelfRound = (locationCode: string) => {
    const docId = openRoundsByShelf[locationCode];
    if (docId) {
      // A write still queued for it is dropped, and one already out that comes back "closed" says nothing more.
      closedRoundsRef.current.add(docId);
    }
    // A hold lifted on this shelf belonged to its old round; a new round on the shelf starts without it.
    Object.keys(liftedStaleRef.current)
      .filter((key) => key.endsWith(`@${locationCode}`))
      .forEach((key) => delete liftedStaleRef.current[key]);
    shelfKeysRef.current.delete(locationCode);
    setOpenRoundsByShelf((current) => {
      const next = { ...current };
      delete next[locationCode];
      return next;
    });
    setAuditTargetsByShelf((current) => {
      const next = { ...current };
      delete next[locationCode];
      return next;
    });
    // Its rows leave the walk, so a recount still owed on them must not keep Confirm off for the shelves left.
    setStaleRecounts((standing) => {
      const kept = Object.entries(standing).filter(([key, line]) => key !== keyOf({ sku: line.sku, locationCode }));
      return kept.length === Object.keys(standing).length ? standing : Object.fromEntries(kept);
    });
  };

  /**
   * A count or confirm landed on a round a colleague had already confirmed or abandoned [SSD-7209]: the server's
   * sentence is shown once per round however many of its writes were refused, the dialog closes, the shelf leaves
   * the walk, and the book is read again. The walk ends when no round of it is left open, counting the rounds
   * refused in the same flush that this render has not dropped yet.
   */
  const closedRoundRefused = (locationCode: string, refusal: ClosedStockTakeRefusal, docId: string) => {
    if (closedRoundsRef.current.has(docId)) {
      return;
    }
    closedRoundsRef.current.add(docId);
    closedRefusalsRef.current[docId] = refusal;
    showError(`Shelf ${shelfLabel(locationCode)}: ${describeClosedStockTake(refusal)}`);
    setConfirmingAudit(false);
    setSignedOff(false);
    dropShelfRound(locationCode);
    const stillOpen = Object.entries(openRoundsByShelf).filter(
      ([shelf, roundId]) => shelf !== locationCode && !closedRoundsRef.current.has(roundId),
    );
    if (stillOpen.length === 0) {
      leaveAudit();
    }
    void load(showZeroQty);
  };
  closedRoundRefusedRef.current = closedRoundRefused;

  /** Closes every open round without applying anything, so an abandoned walk is on record [D-n]. */
  const abandonAudit = async () => {
    setAuditBusy(true);
    try {
      for (const [locationCode, docId] of Object.entries(openRoundsByShelf)) {
        await adminClient.cancelStockTake(docId, ABANDON_REASON);
        shelfKeysRef.current.delete(locationCode);
        setOpenRoundsByShelf((current) => {
          const next = { ...current };
          delete next[locationCode];
          return next;
        });
        setAuditTargetsByShelf((current) => {
          const next = { ...current };
          delete next[locationCode];
          return next;
        });
      }
      leaveAudit();
      showWarning('The stock audit was abandoned; no counts were applied.');
    } catch (failure) {
      showFailure(failure, 'The audit could not be closed.');
    } finally {
      setAuditBusy(false);
      setExiting(false);
    }
  };

  /**
   * Each shelf is signed off on its own (SSD-7212, decision D8): a refused shelf leaves the others confirmed,
   * every shelf is still attempted, and the outcome says how many stood and which failed and why. A confirmed
   * shelf leaves the walk as it goes, so confirming again re-sends only the shelves still open. A lost answer
   * is the one exception: whether that shelf was confirmed is unknown, so the walk stops there and says so.
   */
  const confirmAudit = async () => {
    if (confirmingAuditRef.current) {
      return;
    }
    confirmingAuditRef.current = true;
    setAuditBusy(true);
    const openRounds = Object.entries(openRoundsByShelf);
    const shelves = openRounds.map(([locationCode]) => `Shelf ${shelfLabel(locationCode)}`);
    let confirmed = 0;
    let dropped = 0;
    let countsFlushed = false;
    const confirmedShelves: string[] = [];
    const failed: BulkItemReason[] = [];
    try {
      await flushCounts();
      countsFlushed = true;
      if (openRounds.every(([, docId]) => closedRoundsRef.current.has(docId))) {
        // Every shelf was closed by someone else while its last counts went: each said so, and the walk has ended.
        return;
      }
      for (const [index, [locationCode, docId]] of openRounds.entries()) {
        if (closedRoundsRef.current.has(docId)) {
          // Closed by a colleague while its counts were flushed: nothing of this walk was recorded on it, and the
          // outcome says so the same way as a shelf found closed at confirm time.
          const refusal = closedRefusalsRef.current[docId];
          dropped += 1;
          failed.push({
            item: shelves[index]!,
            reason: refusal
              ? describeClosedStockTake(refusal)
              : 'A colleague closed this shelf before it was confirmed.',
          });
          continue;
        }
        try {
          await adminClient.confirmStockTake(docId);
        } catch (failure) {
          if (isAxiosError(failure) && (!failure.response || failure.response.status >= 500)) {
            // Whether this shelf was confirmed is unknown, so the walk stops here; what came before is reported as it was.
            const outcome = interruptedSequenceResult('Stock Audit', {
              confirmed: confirmedShelves,
              failed,
              unknown: shelves[index]!,
              notAttempted: shelves.slice(index + 1),
            });
            showError(outcome.text, outcome.details);
            setConfirmingAudit(false);
            setSignedOff(false);
            if (confirmed + dropped > 0) {
              await load(showZeroQty);
            }
            return;
          }
          const refusal = staleStockRefusalOf(failure);
          const closed = closedStockTakeRefusalOf(failure);
          if (refusal) {
            // Nothing on this shelf was adjusted and its round stays open: the moved rows are counted again, then confirmed.
            markStaleRecounts(docId, locationCode, refusal.staleLines);
            failed.push({
              item: shelves[index]!,
              reason: `${describeStaleStock(refusal.staleLines)} Enter a new count for these items.`,
            });
          } else if (closed) {
            // A colleague sealed or abandoned this shelf's round, so there is nothing left on it to confirm.
            dropShelfRound(locationCode);
            dropped += 1;
            failed.push({ item: shelves[index]!, reason: describeClosedStockTake(closed) });
          } else {
            failed.push({
              item: shelves[index]!,
              reason: describeFailure(failure, 'The audit could not be recorded.'),
            });
          }
          continue;
        }
        confirmed += 1;
        confirmedShelves.push(shelves[index]!);
        dropShelfRound(locationCode);
      }
      setConfirmingAudit(false);
      if (failed.length === 0) {
        leaveAudit();
        showSuccess(formatBulkResult('Stock Audit', confirmed).text);
        await load(showZeroQty);
        return;
      }
      setSignedOff(false);
      const names = failed.map((entry) => entry.item.replace(/^Shelf /, '')).join(', ');
      const text = `Stock Audit: ${confirmed} of ${openRounds.length} shelves confirmed, ${failed.length} failed (${names}). Confirming again sends only the shelves still open.`;
      const details = failed.map(({ item, reason }) => `${item} — ${reason}`);
      if (confirmed > 0) {
        showWarning(text, details);
      } else {
        showError(text, details);
      }
      if (confirmed + dropped === openRounds.length) {
        // Every shelf is either confirmed or closed by someone else: nothing is left to walk.
        leaveAudit();
      }
      if (confirmed + dropped > 0) {
        await load(showZeroQty);
      }
    } catch (failure) {
      // The flush failed: a stale count already said what happened; anything else is said here. A closed round
      // never lands here, since its write resolves once the shelf has said so and left the walk.
      if (!countsFlushed) {
        setConfirmingAudit(false);
        setSignedOff(false);
        if (!staleStockRefusalOf(failure)) {
          showFailure(failure, UNCONFIRMED_BULK_RESULT);
        }
      }
    } finally {
      confirmingAuditRef.current = false;
      setAuditBusy(false);
    }
  };

  const relocate = async (row: InventoryScreenRow, toLocation: string) => {
    // A blur fires on every focus change, so without this an operator tabbing away twice moves the stock twice.
    if (relocatingRef.current.has(keyOf(row))) {
      return;
    }
    relocatingRef.current.add(keyOf(row));
    try {
      await adminClient.moveStock({
        sku: row.sku,
        fromLocation: row.locationCode,
        toLocation,
        qty: row.onHand,
        idempotencyKey: uuidv4(),
      });
      const next = await load(
        showZeroQty,
        `Location saved — ${row.sku}: ${shelfLabel(row.locationCode)} → ${shelfLabel(toLocation)}.`,
      );
      // The move can empty the line the list is narrowed to, and a filter over nothing reads as lost stock [D-p].
      if (line !== 'all' && next && !next.some((entry) => inLine(entry, line))) {
        setLine('all');
      }
    } catch (failure) {
      // The cell goes back to the shelf the server still holds: a refused move must not read as an applied one.
      setLocationDrafts((drafts) => {
        const rest = { ...drafts };
        delete rest[keyOf(row)];
        return rest;
      });
      showFailure(failure, 'The location change was refused.');
    } finally {
      relocatingRef.current.delete(keyOf(row));
    }
  };

  const loadHolders = useCallback(
    async (sku: string, locationCode: string) => (await adminClient.listReservationHolders(sku, locationCode)).holders,
    [adminClient],
  );

  const showHolders = async (sku: string, locationCode: string) => {
    const requestId = ++reservedRequestRef.current;
    reservedSkuRef.current = sku;
    setReservedSku(sku);
    setReservedLocation(locationCode);
    setHolders([]);
    setHoldersFailed(false);
    setHoldersLoading(true);
    try {
      // A sku reserved on two shelves has two holder sets, so the shelf rides on the wire [T13].
      const result = await adminClient.listReservationHolders(sku, locationCode);
      if (requestId === reservedRequestRef.current && reservedSkuRef.current === sku) {
        setHolders(result.holders);
      }
    } catch {
      // A failed read is a finding, never the empty state: "nobody holds this" would be a lie.
      if (requestId === reservedRequestRef.current) {
        setHoldersFailed(true);
      }
    } finally {
      if (requestId === reservedRequestRef.current) {
        setHoldersLoading(false);
      }
    }
  };

  const release = async (holder: ReservationHolder) => {
    const sku = reservedSkuRef.current;
    if (!releaseDraft.reason.trim() || releaseBusy || !sku || releaseInFlightRef.current) {
      return;
    }
    releaseInFlightRef.current = true;
    const restockQty = Number(releaseDraft.restockQty.trim());
    setReleaseBusy(`${holder.orderId}-${holder.lineNo}`);
    try {
      await adminClient.releaseReservation({
        orderId: holder.orderId,
        lineNo: holder.lineNo,
        reason: releaseDraft.reason.trim(),
        restock: releaseDraft.restock,
        // A write-off names neither a shelf nor a quantity: the units are not going anywhere.
        ...(releaseDraft.restock
          ? {
              ...(releaseDraft.restockQty.trim() && Number.isFinite(restockQty) ? { restockQty } : {}),
              locationCode: reservedLocation,
            }
          : {}),
      });
      await showHolders(sku, reservedLocation);
      await load(showZeroQty, `Reservation released — order ${holder.orderId}, line ${holder.lineNo}.`);
    } catch (failure) {
      showFailure(failure, 'The release was refused.');
    } finally {
      releaseInFlightRef.current = false;
      setReleaseBusy(null);
    }
  };

  /**
   * Reads a page of the audit log; the first page replaces the list and a later one is appended under the same filter.
   * Closing, reopening or refiltering starts a new listing, and a page that answers for an older one is dropped so it
   * cannot append stale rounds or overwrite the cursor.
   */
  const loadRounds = async (origin: StockTakeOrigin | 'all', before: string | null) => {
    const listing = roundsListingRef.current;
    setRoundsLoading(true);
    try {
      const result = await adminClient.listStockTakes({
        limit: ROUNDS_PAGE_SIZE,
        ...(origin !== 'all' ? { origin } : {}),
        ...(before ? { before } : {}),
      });
      if (listing !== roundsListingRef.current) {
        return;
      }
      setRounds((shown) => (before ? [...shown, ...result.rounds] : result.rounds));
      setRoundsCursor(result.nextCursor ?? null);
    } catch {
      if (listing === roundsListingRef.current) {
        showError('Could not read the audit rounds.');
      }
    } finally {
      if (listing === roundsListingRef.current) {
        setRoundsLoading(false);
      }
    }
  };

  const closeRounds = () => {
    roundsListingRef.current += 1;
    setRoundsLoading(false);
    setShowRounds(false);
  };

  const openRounds = async () => {
    roundsListingRef.current += 1;
    setShowRounds(true);
    setRounds([]);
    setRoundsCursor(null);
    await loadRounds(roundsOrigin, null);
  };

  const filterRounds = (origin: StockTakeOrigin | 'all') => {
    roundsListingRef.current += 1;
    setRoundsOrigin(origin);
    setRounds([]);
    setRoundsCursor(null);
    void loadRounds(origin, null);
  };

  const exportStockStatus = async () => {
    if (exportInFlightRef.current) {
      return;
    }
    exportInFlightRef.current = true;
    try {
      // Mid-audit the export is the walk itself: the filtered rows with their counts and diffs [SSD-7212]. Otherwise
      // the endpoint answers JSON and the download is named .csv, so the screen converts before saving.
      const csv = auditOn ? auditCsv(auditRows, counted) : stockStatusCsv(await stockClient.exportStockStatusRows());
      if (csv === null) {
        showError(
          auditOn ? 'There are no rows in this audit to export.' : 'There is no stock to export.',
          undefined,
          AUTO_DISMISS,
        );
        return;
      }
      if (typeof URL.createObjectURL !== 'function') {
        showError('This browser cannot start the stock export download.');
        return;
      }
      const href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = auditOn ? 'stock-audit.csv' : 'stock-status.csv';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoking in the same tick can cancel the download the click just started.
      setTimeout(() => URL.revokeObjectURL(href), 0);
      showSuccess(auditOn ? 'Audit export download started.' : 'Stock export download started.');
    } catch {
      showError('The export failed.');
    } finally {
      exportInFlightRef.current = false;
    }
  };

  /**
   * Enter commits the count and drops to the next shelf; Escape puts the cell back to the last saved
   * reading. The floor counts with a scanner in one hand, so the column has to be walked without a mouse.
   */
  const onCountKeyDown = (event: KeyboardEvent<HTMLInputElement>, row: InventoryScreenRow, index: number) => {
    const key = keyOf(row);
    if (event.key === 'Enter') {
      event.preventDefault();
      const docId = openRoundsByShelf[row.locationCode];
      if (docId) {
        void persistCount(docId, row, countedRef.current[key] ?? '').catch(() => undefined);
      }
      countInputsRef.current[index + 1]?.focus();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      const docId = openRoundsByShelf[row.locationCode];
      setCounted((entries) => ({
        ...entries,
        [key]: (docId && persistedRef.current[countStateKey(docId, key)]) ?? String(row.onHand),
      }));
    }
  };

  const tabs: Array<[InventoryTab, string]> = [
    ['current', 'Current Stocks'],
    ['history', 'Stock History'],
    ['inbound', 'Inbound Stock'],
    ['outbound', 'Stock Adjustment'],
  ];

  return (
    <div className="wms-wireframe ff-ultimate wms-inventory">
      <div className="ptitle">
        <h2>WMS — Inventory</h2>
        <span className="sp" />
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-label="Refresh the stock list"
          disabled={loading}
          onClick={() => {
            setSortEpoch((epoch) => epoch + 1);
            void load(showZeroQty);
          }}
        >
          <RefreshIcon />
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-gray btn-sm"
          aria-label={auditOn ? 'Export Audit' : 'Export Stock Status'}
          onClick={() => void exportStockStatus()}
        >
          <DownloadIcon />
          {auditOn ? 'Export Audit' : 'Export Stock Status'}
        </button>
        {tab === 'current' && (
          <button
            type="button"
            className="btn btn-line btn-sm"
            aria-label="Import Locations"
            /* A walk counts shelves as they stand, so nothing moves them in bulk mid-audit. */
            disabled={auditOn}
            onClick={() => setImportingLocations(true)}
          >
            <UploadIcon />
            Import Locations
          </button>
        )}
      </div>

      <div className="subtabs">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`${key === 'current' ? 'newtab ' : ''}${tab === key ? 'on' : ''}`.trim()}
            /* The open tab clicked again keeps its search; another tab opens empty, as it always has. */
            onClick={() => key !== tab && showInAddress(key, null)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div role="alert" className="note warn">
          {error}
        </div>
      )}

      {tab === 'current' && (
        <>
          {needsFixing.length > 0 && (
            <NeedsFixingFirst
              rows={needsFixing}
              auditOn={auditOn}
              openRoundsByShelf={openRoundsByShelf}
              auditBusy={auditBusy}
              fixingKey={fixing}
              savedKey={savedKey}
              busy={adjustBusy}
              onToggleFix={(row) => {
                setSavedKey(null);
                setAdjusting(null);
                setFixing(fixing === keyOf(row) ? null : keyOf(row));
              }}
              onApply={(row, countedQty, reason, expectedOnHand) =>
                void applyAdjustment(row, countedQty, reason, expectedOnHand, true)
              }
              onCancel={() => setFixing(null)}
              conflict={adjustConflict?.key === fixing ? adjustConflict : null}
              onShowHolders={(row) => void showHolders(row.sku, row.locationCode)}
              loadHolders={loadHolders}
            />
          )}

          <div className="audit-banner">
            <span>
              Stock Audit (stock-take) mode — the floor enters physical counts and the screen works out the difference
              against the book. Confirming writes each difference to history as an ADJUST event.
            </span>
            <span className="sp" />
            <button
              type="button"
              className="btn btn-line btn-sm"
              disabled={auditBusy || (!auditOn && visible.length === 0)}
              onClick={() => (auditOn ? setExiting(true) : void startAudit())}
            >
              {auditOn ? 'Exit Stock Audit' : 'Start Stock Audit'}
            </button>
            <button type="button" className="btn btn-gray btn-sm" onClick={() => void openRounds()}>
              Past Audit Logs
            </button>
          </div>

          <div className="cs-controls">
            <input
              className="inp"
              aria-label="Search SKU or product name"
              aria-description="Enter an exact location code to see its physical stock card."
              placeholder="Search SKU / product name / exact location"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <select
              className="sel"
              aria-label="Location line"
              value={line}
              onChange={(event) => setLine(event.target.value)}
            >
              <option value="all">All Locations</option>
              {locationLines(rows).map((option) => (
                <option key={option} value={option}>
                  Line {option}
                </option>
              ))}
              <option value={UNASSIGNED_LINE}>Unassigned</option>
            </select>
            <select
              className="sel"
              aria-label="Sourcing route"
              value={route}
              onChange={(event) => setRoute(event.target.value)}
            >
              <option value="all">All Sourcing Routes</option>
              {sourcingRouteOptions(rows).map((option) => (
                <option key={option} value={option}>
                  {routeLabel(option)}
                </option>
              ))}
            </select>
            <label className="zero" htmlFor="inventory-show-zero">
              <input
                id="inventory-show-zero"
                type="checkbox"
                checked={showZeroQty}
                onChange={(event) => setShowZeroQty(event.target.checked)}
              />
              Show zero-quantity rows
            </label>
            <span className="sp" />
            {holdingOrder && !auditOn && (
              <span className="blocked-note held-order">
                Edited rows stay in place. Sort again or Refresh to reorder.
              </span>
            )}
            {hiddenZeroQty > 0 && (
              <span className="blocked-note">{`${hiddenZeroQty} zero-quantity ${
                hiddenZeroQty === 1 ? 'row' : 'rows'
              } hidden`}</span>
            )}
          </div>

          {!auditOn && cardLocation !== null && (
            <LocationStockCard rows={cardRows} locationCode={cardLocation} complete={cardComplete} />
          )}

          {pickabilityUnreported && (
            <p className="unreported">
              Pickability is not reported by this server — Available may include virtual locations.
            </p>
          )}

          <div className={adjusting ? 'table-wrap stock-adjusting' : 'table-wrap'}>
            <table className="tbl fit" aria-label="Current stocks">
              {/* Laid out from these widths so every column fits the viewport: a long sku or name wraps instead of pushing the figures a scroll away [SSD-7215 follow-up]. */}
              <colgroup>
                {(auditOn ? AUDIT_COLUMN_WIDTHS : BROWSE_COLUMN_WIDTHS).map(([column, width]) => (
                  <col key={column} style={{ width }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Image</th>
                  <th className="name-col">Product Name</th>
                  <th className="name-col">Product Name KR</th>
                  <th className="size-col">Size</th>
                  <th>Barcode</th>
                  <th>Sourcing Route</th>
                  <th
                    aria-sort={
                      auditOn
                        ? undefined
                        : sortMode === 'location-asc'
                          ? 'ascending'
                          : sortMode === 'location-desc'
                            ? 'descending'
                            : 'none'
                    }
                  >
                    {auditOn ? (
                      'Location'
                    ) : (
                      <button
                        type="button"
                        className="btn btn-line btn-sm"
                        onClick={() =>
                          setSortMode((mode) => (mode === 'location-asc' ? 'location-desc' : 'location-asc'))
                        }
                      >
                        Location{' '}
                        <span aria-hidden="true">
                          {sortMode === 'location-asc' ? '↑' : sortMode === 'location-desc' ? '↓' : '↕'}
                        </span>
                      </button>
                    )}
                  </th>
                  <th
                    aria-sort={
                      auditOn
                        ? undefined
                        : sortMode === 'total-desc'
                          ? 'descending'
                          : sortMode === 'total-asc'
                            ? 'ascending'
                            : 'none'
                    }
                  >
                    {auditOn ? (
                      'Total'
                    ) : (
                      <button
                        type="button"
                        className="btn btn-line btn-sm"
                        onClick={() => setSortMode((mode) => (mode === 'total-desc' ? 'total-asc' : 'total-desc'))}
                      >
                        Total{' '}
                        <span aria-hidden="true">
                          {sortMode === 'total-desc' ? '↓' : sortMode === 'total-asc' ? '↑' : '↕'}
                        </span>
                      </button>
                    )}
                  </th>
                  <th>Reserved</th>
                  <th>Available</th>
                  {auditOn && <th>Counted Qty</th>}
                  {auditOn && <th>Diff</th>}
                </tr>
              </thead>
              <tbody>
                {auditRows.map((row, index) => {
                  const key = keyOf(row);
                  const diff = countedDiff(row.onHand, counted[key] ?? '');
                  const countError = countInputError(counted[key] ?? '');
                  const oversold = isOversold(row);
                  const stale = staleRecounts[key];
                  const rowClass = [
                    row.pickable === false ? 'nonpickable' : '',
                    stale ? 'stale-recount' : '',
                    savedKey === key ? 'row-saved' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <tr key={key} className={rowClass || undefined}>
                      <td className="num">{row.sku}</td>
                      <td>
                        {row.imageUrl ? (
                          <ProductImageZoom
                            src={row.imageUrl}
                            label={[row.brand, row.name].filter(Boolean).join(' ') || row.sku}
                          >
                            <img className="thumb" src={row.imageUrl} alt="" />
                          </ProductImageZoom>
                        ) : (
                          <div className="thumb">IMG</div>
                        )}
                      </td>
                      <td className="name-col">
                        {row.brand && <b>{row.brand} </b>}
                        <span>{row.name ?? MISSING}</span>
                        {row.productLink && (
                          <a
                            className="inv-product-link"
                            href={row.productLink}
                            target="_blank"
                            rel="noreferrer"
                            title="Open the product page on skin-seoul.com"
                            aria-label={`Open ${row.name ?? row.sku} on skin-seoul.com`}
                          >
                            <ExternalLink aria-hidden="true" />
                          </a>
                        )}
                      </td>
                      <td className="name-col">
                        <span>{row.nameKor ?? MISSING}</span>
                      </td>
                      <td className="size-col">{row.size ?? MISSING}</td>
                      <td className="num">
                        <BarcodeCell
                          sku={row.sku}
                          barcode={row.barcode}
                          productId={row.productId}
                          onSave={saveBarcode}
                        />
                      </td>
                      <td>{routesText(row)}</td>
                      <td>
                        <input
                          className="loc-in"
                          aria-label={`Location ${row.sku}`}
                          placeholder="Unassigned"
                          /* The floor is typing counts: a shelf code reached by Tab is a move nobody meant to make. */
                          readOnly={auditOn}
                          tabIndex={auditOn ? -1 : undefined}
                          value={locationDrafts[key] ?? shelfInputValue(row.locationCode)}
                          onChange={(event) =>
                            setLocationDrafts((drafts) => ({ ...drafts, [key]: event.target.value }))
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                              event.preventDefault();
                              event.currentTarget.blur();
                            }
                          }}
                          onBlur={(event) => {
                            const next = normalizeLocationCode(event.target.value);
                            if (next && next !== row.locationCode) {
                              // A code the warehouse has no grammar for is refused here, before the shelf moves [D-c].
                              if (!isLocationCode(next)) {
                                showError(LOCATION_CODE_ERROR);
                                return;
                              }
                              void relocate(row, next);
                              return;
                            }
                            // An emptied cell takes the row off its shelf: the stock parks as Unassigned, the bucket the
                            // cutover left unshelved stock in, and stays on hand and reservable [SSD-7213, decision D9].
                            if (!next && !isUnassignedShelf(row.locationCode)) {
                              void relocate(row, UNASSIGNED_SHELF);
                              return;
                            }
                            // Nothing was sent, so the cell goes back to the shelf the server holds.
                            setLocationDrafts((drafts) => {
                              const rest = { ...drafts };
                              delete rest[key];
                              return rest;
                            });
                          }}
                        />
                        {row.pickable === false && <span className="tag tag-route"> Non-pickable</span>}
                      </td>
                      <td className="num tot-cell">
                        <span className="tot-v">{row.onHand}</span>
                        {!auditOn && (
                          <button
                            type="button"
                            className="tot-edit"
                            aria-label={`Adjust stock quantity ${row.sku}`}
                            disabled={auditBusy}
                            onClick={() => {
                              setSavedKey(null);
                              setFixing(null);
                              setAdjusting(adjusting === key ? null : key);
                            }}
                          >
                            <PencilIcon />
                          </button>
                        )}
                        {savedKey === key && <span className="tot-saved">✓ Saved</span>}
                        {adjusting === key && (
                          <StockAdjustPopover
                            row={row}
                            busy={adjustBusy}
                            onApply={(countedQty, reason, expectedOnHand) =>
                              void applyAdjustment(row, countedQty, reason, expectedOnHand)
                            }
                            onCancel={() => setAdjusting(null)}
                            loadHolders={loadHolders}
                            conflict={adjustConflict?.key === key ? adjustConflict : null}
                          />
                        )}
                      </td>
                      <td className="num res-qty">
                        {row.reserved > 0 ? (
                          <button
                            type="button"
                            className="reslink"
                            aria-label={`Reserved ${row.reserved}`}
                            onClick={() => void showHolders(row.sku, row.locationCode)}
                          >
                            {row.reserved}
                          </button>
                        ) : (
                          0
                        )}
                      </td>
                      {/* An oversold shelf is a book defect, so it never wears the sellable green — even at the 0 a virtual shelf reports [E-117]. */}
                      <td
                        className={oversold ? 'num av-qty neg' : 'num av-qty'}
                        title={oversold ? `Reserved ${row.reserved} exceeds ${row.onHand} on hand` : undefined}
                      >
                        {oversold ? `⚠ ${row.available}` : row.available}
                      </td>
                      {auditOn && (
                        <td>
                          <input
                            className={countError ? 'qty-in bad' : 'qty-in'}
                            type="number"
                            min="0"
                            max={MAX_COUNT}
                            step="1"
                            aria-label={`Counted quantity ${row.sku}`}
                            aria-invalid={countError !== null}
                            title={countError ?? undefined}
                            ref={(element) => {
                              countInputsRef.current[index] = element;
                            }}
                            value={counted[key] ?? ''}
                            disabled={auditBusy}
                            onChange={(event) => {
                              const docId = openRoundsByShelf[row.locationCode];
                              if (docId) {
                                const stateKey = countStateKey(docId, key);
                                // A recount is sent even when it lands on the same number: it re-snapshots the shelf.
                                if (prefilledRef.current[stateKey] !== undefined || staleRecounts[key]) {
                                  delete prefilledRef.current[stateKey];
                                  delete persistedRef.current[stateKey];
                                  delete scheduledCountsRef.current[stateKey];
                                  delete countSaveFailuresRef.current[stateKey];
                                }
                              }
                              // Only a typed number lifts the hold: a box emptied again is still uncounted, and the
                              // round's old line must never be confirmed in its place, so emptying it before the
                              // count is saved puts the hold back [SSD-7209].
                              if (staleRecounts[key] && event.target.value.trim() !== '') {
                                liftedStaleRef.current[key] = staleRecounts[key];
                                setStaleRecounts((standing) => {
                                  const rest = { ...standing };
                                  delete rest[key];
                                  return rest;
                                });
                              } else if (
                                !staleRecounts[key] &&
                                event.target.value.trim() === '' &&
                                liftedStaleRef.current[key]
                              ) {
                                const held = liftedStaleRef.current[key];
                                delete liftedStaleRef.current[key];
                                setStaleRecounts((standing) => ({ ...standing, [key]: held }));
                              }
                              setCounted((entries) => ({ ...entries, [key]: event.target.value }));
                            }}
                            onKeyDown={(event) => onCountKeyDown(event, row, index)}
                          />
                          {stale && (
                            <div className="recount-note" role="alert">
                              {`Stock changed: counted against ${stale.countedAgainst}, now ${stale.current}${
                                stale.changedBy ? ` by ${stale.changedBy}` : ''
                              }. Enter a new count.`}
                            </div>
                          )}
                        </td>
                      )}
                      {auditOn && (
                        <td className={`num ${diff !== null && diff < 0 ? 'diff-neg' : 'diff-pos'}`}>
                          {diff === null ? MISSING : diff > 0 ? `+${diff}` : diff}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {auditRows.length === 0 && (
                  <tr>
                    <td colSpan={auditOn ? 13 : 11} className="blocked-note">
                      {loading ? (
                        'Loading the stock…'
                      ) : hasActiveFilters ? (
                        <>
                          No products match these filters{' '}
                          <button type="button" className="btn btn-line btn-sm" onClick={clearFilters}>
                            Clear filters
                          </button>
                        </>
                      ) : (
                        'No stock is available.'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {auditOn && (
            <div className="auditsummary">
              <span>{`Adjustments: ${tally.adjustments} (−${tally.decreases} / +${tally.increases})`}</span>
              {/* A shelf the floor found level still has to be sealed, so a zero-variance walk can be confirmed [D-n]. */}
              <button
                type="button"
                className="btn btn-blue btn-sm"
                disabled={auditBusy || hasStaleRecounts}
                title={
                  hasStaleRecounts
                    ? `Enter a new count for the highlighted items first: ${Object.values(staleRecounts)
                        .map((line) => line.sku)
                        .join(', ')}.`
                    : undefined
                }
                onClick={() => {
                  if (hasClearedSavedCounts) {
                    showError(`A saved count cannot be cleared. ${COUNT_ERROR}`);
                    return;
                  }
                  if (hasInvalidCounts) {
                    showError(`Fix the highlighted counts. ${COUNT_ERROR}`, undefined, AUTO_DISMISS);
                    return;
                  }
                  // A sign-off covers only the shortfalls it was given, so every opening asks again.
                  setSignedOff(false);
                  setConfirmingAudit(true);
                }}
              >
                Confirm Audit Differences
              </button>
            </div>
          )}
        </>
      )}

      {tab === 'history' && (
        <StockHistoryPane
          stockClient={stockClient}
          adminClient={adminClient}
          stockedRows={rows}
          onFailure={(text) => showError(text)}
          addressed={historySearch}
          onSearch={(search) => showInAddress('history', search)}
        />
      )}

      {(tab === 'inbound' || tab === 'outbound') && (
        <MovementFormPane
          key={tab}
          direction={tab}
          stockClient={stockClient}
          adminClient={adminClient}
          operator={operator}
          onRecorded={(text) => showSuccess(text)}
          onFailure={(text) => showError(text)}
        />
      )}

      {reservedSku && (
        <ReservedOrdersModal
          sku={reservedSku}
          locationCode={reservedLocation}
          holders={holders}
          loading={holdersLoading}
          failed={holdersFailed}
          draft={releaseDraft}
          busyKey={releaseBusy}
          oversold={shortSkus.includes(reservedSku)}
          onDraftChange={setReleaseDraft}
          onRelease={(holder) => void release(holder)}
          onRetry={() => void showHolders(reservedSku, reservedLocation)}
          onClose={() => {
            reservedRequestRef.current += 1;
            reservedSkuRef.current = null;
            setReservedSku(null);
          }}
        />
      )}

      {showRounds && (
        <PastAuditLogsModal
          rounds={rounds}
          loading={roundsLoading}
          origin={roundsOrigin}
          hasMore={roundsCursor !== null}
          onOriginChange={filterRounds}
          onLoadMore={() => void loadRounds(roundsOrigin, roundsCursor)}
          onClose={closeRounds}
        />
      )}

      {importingLocations && (
        <LocationImportModal
          client={adminClient}
          stockClient={stockClient}
          onClose={() => setImportingLocations(false)}
          onApplied={() => void load(showZeroQty)}
        />
      )}

      {confirmingAudit && (
        <ConfirmAuditModal
          differences={differences}
          shortfalls={shortfalls}
          tally={tally}
          busy={auditBusy}
          signedOff={signedOff}
          onSignOffChange={setSignedOff}
          onConfirm={() => void confirmAudit()}
          notifications={<FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />}
          onClose={() => {
            setConfirmingAudit(false);
            setSignedOff(false);
          }}
        />
      )}

      {exiting && (
        <AbandonAuditModal
          shelves={Object.keys(openRoundsByShelf).length}
          busy={auditBusy}
          onAbandon={() => void abandonAudit()}
          onClose={() => setExiting(false)}
        />
      )}

      {!confirmingAudit && <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />}
    </div>
  );
}
